import { useSearchParams } from 'react-router-dom';
import { RestaurantTitleSuffix } from '@/components/restaurant/RestaurantTitleSuffix';
import BookingsTab from '@/components/booking/BookingsTab';
import SchedulesTab from '@/components/booking/SchedulesTab';
import SectionsTab from '@/components/booking/SectionsTab';
import RulesTab from '@/components/booking/RulesTab';
import SpecialDaysTab from '@/components/booking/SpecialDaysTab';
import { useRestaurantId } from '@/hooks/useRestaurantId';
import { AlertTriangle } from 'lucide-react';

const TABS = [
  { id: 'bookings', label: 'Bookings' },
  { id: 'schedules', label: 'Schedules' },
  { id: 'sections', label: 'Sections' },
  { id: 'rules', label: 'Rules' },
  { id: 'special-days', label: 'Special Days' },
] as const;

type TabId = typeof TABS[number]['id'];

function isValidTab(value: string | null): value is TabId {
  return TABS.some((tab) => tab.id === value);
}

export default function BookingConfigPage() {
  const restaurantId = useRestaurantId();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isValidTab(tabParam) ? tabParam : 'bookings';

  const setActiveTab = (tab: TabId) => setSearchParams({ tab }, { replace: true });

  if (restaurantId === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Invalid restaurant ID</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Booking Configuration
            <RestaurantTitleSuffix restaurantId={restaurantId} />
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage booking schedules, sections, rules, and special days
          </p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'bookings' && <BookingsTab restaurantId={restaurantId} />}
      {activeTab === 'schedules' && <SchedulesTab restaurantId={restaurantId} />}
      {activeTab === 'sections' && <SectionsTab restaurantId={restaurantId} />}
      {activeTab === 'rules' && <RulesTab restaurantId={restaurantId} />}
      {activeTab === 'special-days' && <SpecialDaysTab restaurantId={restaurantId} />}
    </div>
  );
}
